import React, { useMemo } from "react";
import { Category, Filters } from "../types";
import { SlidersHorizontal, X } from "lucide-react";
import { capitalizeWords, getTodayFormatted } from "../utils/utils";

interface AdvancedFiltersProps {
  filters: Filters; // Assurez-vous que le type Filters est accessible
  onFilterChange: (key: keyof Filters, value: Filters[keyof Filters]) => void;
  categories: Category[];
  t: any;
  isOpen: boolean;
  onClose: () => void;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters,
  onFilterChange,
  categories,
  t,
  isOpen,
  onClose,
}) => {
  const allCategories = useMemo(
    () => [{ id: "all", name: "all" }, ...categories],
    [categories, t]
  );

  // Le panneau de filtres (translate/full height/fixed)
  const panelClasses = `fixed top-0 left-0 h-full w-full md:w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
    isOpen ? "translate-x-0" : "-translate-x-full"
  } md:static md:translate-x-0 md:p-0 md:bg-transparent md:shadow-none md:block`;

  return (
    <>
      {/* 1. Overlay sombre pour mobile (z-40). Doit être positionné en tant que SIBLING LOGIQUE du panneau ou enveloppé par une div. 
          Pour garantir que le panneau de filtre est au-dessus, nous le sortons du div principal et lui donnons z-40.
          On le met dans un fragment car on retourne plusieurs éléments, mais il reste positionné fixement. */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-30 z-40 md:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* 2. Le panneau de filtre lui-même (z-50) */}
      <div className={panelClasses}>
        <div className="md:sticky md:top-24 space-y-8 bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          {/* Titre Mobile: Ajout de l'icône et du flex pour l'alignement */}
          <div className="flex justify-between items-center mb-6 md:hidden">
            <h3 className="flex items-center space-x-3 text-2xl font-bold text-black">
              <SlidersHorizontal className="w-7 h-7 text-orange-500" />{" "}
              {/* Icône réintégrée */}
              <span>{t("filters.title")}</span>
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          {/* Titre Desktop: OK */}
          <div className="hidden md:flex items-center space-x-3 mb-8 pb-3 border-b border-gray-200">
            <SlidersHorizontal className="w-6 h-6 text-orange-500" />
            <h3 className="text-xl font-bold text-black">
              {t("filters.title")}
            </h3>
          </div>

          {/* Recherche par nom */}
          <div className="space-y-2">
            <label
              htmlFor="search"
              className="text-sm font-medium text-gray-700"
            >
              {t("filters.dishName")}
            </label>
            <input
              id="search"
              type="text"
              placeholder={t("filters.searchPlaceholder")}
              value={filters.searchQuery}
              onChange={(e) => onFilterChange("searchQuery", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 transition duration-150"
            />
          </div>

          {/* Catégorie (Radio/Selection Unique) */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-800">
              {t("filters.category")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {allCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => onFilterChange("category", cat.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                    filters.category === cat.name
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50 hover:border-orange-200"
                  }`}
                >
                  {cat.name === "all"
                    ? capitalizeWords(t("menu.categories.all"))
                    : capitalizeWords(cat.name)}
                </button>
              ))}
            </div>
          </div>

          {/* Recherche par date de disponibilité  */}
          <div className="space-y-2">
            <label
              htmlFor="availabilityDate"
              className="text-sm font-medium text-gray-700"
            >
              {t("filters.availabilityDate")}
            </label>
            <input
              id="availabilityDate"
              min={getTodayFormatted()}
              defaultValue={getTodayFormatted()}
              type="date"
              placeholder={t("filters.searchPlaceholder")}
              value={filters.availabilityDate}
              onChange={(e) =>
                onFilterChange("availabilityDate", e.target.value)
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 transition duration-150"
            />
          </div>

          {/* Filtre de prix */}
          <div className="space-y-3">
            <h4 className="text-base font-semibold text-gray-800">
              {t("filters.priceRange")} (CAD)
            </h4>
            <div className="flex space-x-3">
              <input
                type="number"
                placeholder={t("filters.minPrice")}
                value={filters.minPrice}
                onChange={(e) =>
                  onFilterChange(
                    "minPrice",
                    e.target.value === "" ? "" : parseFloat(e.target.value)
                  )
                }
                className="w-1/2 p-3 border border-gray-300 rounded-lg"
                min="0"
              />
              <input
                type="number"
                placeholder={t("filters.maxPrice")}
                value={filters.maxPrice}
                onChange={(e) =>
                  onFilterChange(
                    "maxPrice",
                    e.target.value === "" ? "" : parseFloat(e.target.value)
                  )
                }
                className="w-1/2 p-3 border border-gray-300 rounded-lg"
                min="0"
              />
            </div>
          </div>

          {/* Plats à venir (Programmed) */}
          {/* <div className="flex items-center justify-between pt-2">
            <label
              htmlFor="futureDishes"
              className="text-base font-semibold text-gray-800 cursor-pointer"
            >
              {t("filters.futureDishes")}
            </label>
            <input
              id="futureDishes"
              type="checkbox"
              checked={filters.showFutureDishes}
              onChange={(e) =>
                onFilterChange("showFutureDishes", e.target.checked)
              }
              className="h-5 w-5 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
            />
          </div> */}
        </div>
      </div>
    </>
  );
};
